import React from 'react';
import { MapPin, Clock, Building2, ClipboardList, ArrowRight, Users, Eye, MessageSquare, Image as ImageIcon, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { timeAgo } from '../../../utils/timeAgo';
import { usePanelByOpportunityId } from '../../panels/hooks/use-panels';
import { useAuth } from '../../../contexts/AuthContext';

const TYPE_COLORS = {
  'Full Time':  'bg-green-50 text-green-700 border border-green-200',
  'Part Time':  'bg-blue-50 text-blue-700 border border-blue-200',
  'Internship': 'bg-purple-50 text-purple-700 border border-purple-200',
  'Contract':   'bg-orange-50 text-orange-700 border border-orange-200',
};

const OpportunityCard = ({ opportunity, onViewDetail, onApply, showActions = false, onClose, userRole }) => {
  const navigate = useNavigate();
  const {
    id,
    title,
    clinicName,
    location,
    type,
    experienceRequired,
    screeningQuestion,
    caseAttachmentUrl,
    isOpen,
    createdAt,
    views,
    _count,
    hasApplied,
  } = opportunity;

  const applicants = _count?.applications || 0;
  const { data: panelData } = usePanelByOpportunityId(id);
  const panel = panelData; // interceptor already unwrapped

  const { user: currentUser } = useAuth();
  const isAuthor = currentUser?.id === opportunity.postedById;

  const [isExpanded, setIsExpanded] = React.useState(false);
  const MAX_LENGTH = 200;
  const isLongQuestion = screeningQuestion?.length > MAX_LENGTH;
  const displayQuestion = isExpanded || !isLongQuestion 
    ? screeningQuestion 
    : `${screeningQuestion.slice(0, MAX_LENGTH)}...`;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-8 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">

      {/* Header Row: Title & Badges */}
      <div className="flex items-start gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h3 className="text-xl font-bold text-gray-900 leading-tight break-words [overflow-wrap:anywhere]">{title}</h3>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${TYPE_COLORS[type] || 'bg-gray-100 text-gray-600'}`}>
              {type}
            </span>
            {!isOpen && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-600 border border-red-200">
                Closed
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Meta Row: Hospital, Location, Time */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500 mb-6">
        <span className="flex items-center gap-1.5 font-medium break-words [overflow-wrap:anywhere]">
          <Building2 size={16} className="text-gray-400 shrink-0" /> {clinicName}
        </span>
        <span className="flex items-center gap-1.5 font-medium break-words [overflow-wrap:anywhere]">
          <MapPin size={16} className="text-gray-400 shrink-0" /> {location}
        </span>
        <span className="flex items-center gap-1.5 font-medium">
          <Clock size={16} className="text-gray-400" /> {timeAgo(createdAt)}
        </span>
      </div>

      {/* Details Row: Experience & Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <div className="text-xs text-gray-400 font-semibold mb-1 uppercase tracking-wider">Experience Required</div>
          <div className="text-sm font-bold text-gray-900 break-words [overflow-wrap:anywhere]">{experienceRequired}</div>
        </div>
        <div>
          <div className="text-xs text-gray-400 font-semibold mb-1 uppercase tracking-wider">Application Stats</div>
          <div className="text-sm font-bold text-gray-900 flex items-center gap-3">
            <span className="flex items-center gap-1"><Eye size={14} className="text-gray-400" /> {views ?? 0} views</span>
            <span className="text-gray-300">•</span>
            <span className="flex items-center gap-1"><Users size={14} className="text-gray-400" /> {applicants} applicants</span>
          </div>
        </div>
      </div>

      {/* Clinical Screening Question Box */}
      {screeningQuestion && (
        <div className="bg-[#faf5ff] rounded-xl p-5 mb-6 border border-[#e9d8fd]">
          <div className="flex items-center gap-2 text-[#6b46c1] font-bold text-sm mb-2">
            <ClipboardList size={16} />
            Clinical Screening Question
          </div>
          <p 
            className="text-gray-700 text-[15px] leading-relaxed font-medium whitespace-pre-wrap break-words overflow-hidden"
            onClick={() => isLongQuestion && setIsExpanded(!isExpanded)}
            style={{ cursor: isLongQuestion ? 'pointer' : 'default', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
          >
            {displayQuestion}
          </p>
          {isLongQuestion && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-[#6b46c1] font-bold text-xs mt-2 hover:underline focus:outline-none"
            >
              {isExpanded ? 'Show less' : 'Read more'}
            </button>
          )}

          {/* Attached Clinical Case Image / Document */}
          {caseAttachmentUrl && (
            <div className="mt-3 pt-3 border-t border-[#e9d8fd]/60">
              <div className="text-xs font-semibold text-[#6b46c1] mb-2 flex items-center gap-1.5">
                <ImageIcon size={14} /> Attached Clinical Case
              </div>
              {/\.(jpg|jpeg|png|webp|gif)(\?.*)?$/i.test(caseAttachmentUrl) ? (
                <div className="relative group rounded-xl overflow-hidden border border-purple-200 bg-white max-h-72">
                  <img 
                    src={caseAttachmentUrl} 
                    alt="Attached Clinical Case" 
                    className="w-full h-full object-cover max-h-72 cursor-pointer transition-transform duration-300 group-hover:scale-105" 
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(caseAttachmentUrl, '_blank');
                    }}
                  />
                </div>
              ) : (
                <a 
                  href={caseAttachmentUrl} 
                  target="_blank" 
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-2 text-xs font-semibold text-purple-700 bg-white px-3 py-2 rounded-lg border border-purple-200 hover:bg-purple-50 transition-colors"
                >
                  <FileText size={16} /> View Case Attachment ↗
                </a>
              )}
            </div>
          )}
        </div>
      )}

      {/* Footer Actions */}
      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="bg-[#f3e8ff] text-[#6b46c1] text-xs font-semibold px-3 py-1.5 rounded-full">
            Case-Based Screening
          </span>
          {/* Discussion Panel badge */}
          {panel && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (!isAuthor && !hasApplied) {
                  alert('You must apply for this opportunity to access its discussion panel.');
                } else {
                  navigate(`/panels/${panel.id}`);
                }
              }}
              className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors border ${
                !isAuthor && !hasApplied
                  ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed opacity-70'
                  : 'bg-indigo-50 text-indigo-600 border-indigo-200 hover:bg-indigo-100'
              }`}
              title={!isAuthor && !hasApplied ? 'Apply first to join discussion' : ''}

            >
              <MessageSquare size={12} /> Discussion Panel
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          {showActions && isOpen && (
            <button
              onClick={() => onClose && onClose(id)}
              className="px-4 py-2 text-sm font-semibold text-orange-600 border border-orange-200 rounded-lg hover:bg-orange-50 transition-colors"
            >
              Close
            </button>
          )}

          {isAuthor ? (
            <span className="px-5 py-2.5 text-sm font-semibold text-[#6b46c1] bg-[#f3e8ff] rounded-xl border border-purple-100">
              Your Posting
            </span>
          ) : hasApplied ? (
            <span className="flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold text-green-700 bg-green-50 border border-green-200 rounded-xl">
              ✓ Applied
            </span>
          ) : isOpen ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onApply) onApply(opportunity);
                else if (onViewDetail) onViewDetail(opportunity);
              }}
              className="flex items-center gap-2 px-6 py-2.5 bg-[#667eea] text-white text-sm font-semibold rounded-xl hover:bg-[#5a67d8] transition-colors shadow-sm"
            >
              Apply Now <ArrowRight size={16} />
            </button>
          ) : (
            <span className="px-5 py-2.5 text-sm font-semibold text-gray-400 bg-gray-100 rounded-xl">
              Position Closed
            </span>
          )}
        </div>
      </div>

    </div>
  );
};

export default OpportunityCard;
